import { apiClient, getErrorMessage } from '@/lib/apiClient';

/**
 * Entity field metadata
 */
export interface EntityFieldMetadata {
  name: string;
  type: string;
  label?: string;
  required: boolean;
}

/**
 * Entity Metadata interface
 */
export interface EntityMetadata {
  entityName: string;
  moduleName: string;
  allowsUserDefinedFields: boolean;
  description?: string;
  fields?: EntityFieldMetadata[];
}

/**
 * Entity Metadata Service
 * Service for accessing entity metadata from DSL specifications
 */
export class EntityMetadataService {
  private readonly baseUrl = '/api/core/entity-metadata';

  /**
   * Get all entities that allow user-defined fields
   */
  async getEntitiesWithUserDefinedFields(): Promise<EntityMetadata[]> {
    try {
      const response = await apiClient.get<EntityMetadata[]>(`${this.baseUrl}/with-udf`);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching entities with UDF support:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Check if an entity allows user-defined fields
   */
  async allowsUserDefinedFields(entityName: string): Promise<boolean> {
    try {
      const response = await apiClient.get<boolean>(`${this.baseUrl}/${entityName}/allows-udf`);
      return response.data ?? false;
    } catch (error) {
      console.error(`Error checking UDF allowance for ${entityName}:`, error);
      return false;
    }
  }

  /**
   * Get full metadata for an entity
   */
  async getEntityMetadata(entityName: string): Promise<EntityMetadata | undefined> {
    try {
      const response = await apiClient.get<EntityMetadata>(`${this.baseUrl}/${entityName}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching metadata for ${entityName}:`, error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get all entities in the registry
   */
  async getAllEntities(): Promise<EntityMetadata[]> {
    try {
      const response = await apiClient.get<EntityMetadata[]>(this.baseUrl);
      return response.data || [];
    } catch (error) {
      console.error('Error fetching all entities:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get field definitions for a specific entity
   */
  async getEntityFields(entityName: string): Promise<EntityFieldMetadata[]> {
    try {
      const response = await apiClient.get<EntityFieldMetadata[]>(`${this.baseUrl}/${entityName}/fields`);
      return response.data || [];
    } catch (error) {
      console.error(`Error fetching fields for ${entityName}:`, error);
      throw new Error(getErrorMessage(error));
    }
  }
}

// Export singleton instance
export const entityMetadataService = new EntityMetadataService();

